import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const slides = [
  {
    id: 1,
    image: '/images/campus/classroom-01.jpg',
    titleKey1: 'hero_title_line1',
    titleKey2: 'hero_title_line2',
    ctaKey: 'hero_cta_discover_piliers',
    ctaLink: '#piliers'
  },
  {
    id: 2,
    image: '/images/campus/entrance.jpg',
    titleKey1: 'hero_title_line1',
    titleKey2: 'hero_title_line2',
    ctaKey: 'hero_cta_apply',
    ctaLink: '/admissions'
  },
  {
    id: 3,
    image: '/images/campus/doc-ceremony-01.jpg',
    titleKey1: 'hero_title_line1',
    titleKey2: 'hero_title_line2',
    ctaKey: 'hero_cta_discover',
    ctaLink: '/programs'
  }
];

export default function HeroSection() {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-slide effect
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="bg-white py-2 md:py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Contained Slider - Compact Height & No Arrows */}
        <div className="relative w-full h-[300px] md:h-[380px] lg:h-[420px] overflow-hidden bg-navy shadow-2xl rounded-sm">
          {/* Slides */}
          {slides.map((slide, index) => (
            <div 
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              {/* Background Image */}
              <img 
                src={slide.image} 
                alt="ESTAD Campus" 
                className="w-full h-full object-cover"
                loading={index === 0 ? "eager" : "lazy"}
              />
              
              {/* NSCC Style Bottom Overlay Bar - Heavy Navy with Gold Accent */}
              <div className="absolute bottom-0 left-0 right-0 bg-navy/95 text-white z-20">
                <div className="px-6 py-5 md:px-10 md:py-8 lg:px-12 lg:py-8 flex flex-col md:flex-row justify-between items-end gap-6">
                  <div className="flex-1 space-y-1 md:space-y-2 animate-in fade-in slide-in-from-bottom-4 duration-700">
                    {/* Gold Accent Pillar (Left) */}
                    <div className="flex gap-4 items-start">
                      <div className="w-1.5 h-10 md:h-16 lg:h-16 bg-gold flex-shrink-0 mt-1"></div>
                      <div className="space-y-1.5 md:space-y-2">
                        <h2 className="text-xl md:text-2xl lg:text-3xl font-black tracking-tight uppercase italic leading-tight">
                          {t(slide.titleKey1)} <span className="text-gold">{t(slide.titleKey2)}</span>
                        </h2>
                        <p className="hidden md:block text-xs lg:text-sm text-white/80 font-medium max-w-2xl leading-relaxed">
                          {t('hero_description_short', "ESTAD University Institute — Votre partenaire pour une éducation d'excellence au cœur de l'Afrique.")}
                        </p>
                        <div className="pt-0.5">
                          <Link 
                            to={slide.ctaLink} 
                            className="inline-flex items-center gap-2 text-gold font-black text-[10px] md:text-xs uppercase tracking-widest hover:text-white transition-all group/btn underline underline-offset-4 decoration-2"
                          >
                            {t(slide.ctaKey)}
                            <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Slider Navigation - Bottom Right like NSCC */}
                  <div className="flex gap-2.5 mb-1 md:mb-0.5">
                    {slides.map((_, dotIdx) => (
                      <button
                        key={dotIdx}
                        onClick={() => setCurrentSlide(dotIdx)}
                        className={`w-2 h-2 rounded-full transition-all duration-300 border-2 ${
                          dotIdx === currentSlide ? 'bg-gold border-gold scale-125' : 'bg-transparent border-white/40 hover:border-white'
                        }`}
                        aria-label={`Go to slide ${dotIdx + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
